import { createAsyncThunk } from "@reduxjs/toolkit";
import { setAuthToken } from "../../utils/tokenHeaders";
import { getApiBaseUrl } from "../../utils/apiBase";
import { userSlice } from "../user/userSlice";

const url = getApiBaseUrl();

const SERVER_UNAVAILABLE_MESSAGE =
  "Сервер временно недоступен. Попробуйте позже или обратитесь в поддержку.";

function authNetworkErrorPayload() {
  return {
    type: "error",
    message: SERVER_UNAVAILABLE_MESSAGE,
  };
}

function parseAuthJson(text) {
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return null;
  }
}

async function parseVideoUploadResponse(response) {
  const raw = await response.text();
  const result = parseAuthJson(raw);

  if (result === null) {
    return rejectWithValuePayload(
      "Сервер вернул некорректный ответ. Попробуйте позже.",
    );
  }

  if (!response.ok || result.type === "error") {
    return rejectWithValuePayload(
      result.message || "Не удалось загрузить видео",
    );
  }

  return { ok: true, data: result };
}

function rejectWithValuePayload(message) {
  return {
    ok: false,
    payload: {
      type: "error",
      message,
    },
  };
}

const CHUNK_SIZE = 5 * 1024 * 1024; // 5 MB chunks
const MAX_CHUNK_RETRIES = 3;

function sendSingleRequestWithProgress({
  endpoint,
  formData,
  headers = {},
  onProgress,
  signal,
}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", endpoint);

    const authHeader = setAuthToken();
    if (authHeader) {
      xhr.setRequestHeader("Authorization", authHeader);
    }

    for (const [key, value] of Object.entries(headers)) {
      xhr.setRequestHeader(key, value);
    }

    if (xhr.upload && typeof onProgress === "function") {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && event.total > 0) {
          const percent = Math.min(
            100,
            Math.max(0, Math.round((event.loaded / event.total) * 100)),
          );
          onProgress(percent);
        }
      };
    }

    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        reject(new Error("Загрузка отменена"));
        return;
      }
      signal.addEventListener("abort", () => {
        xhr.abort();
        reject(new Error("Загрузка отменена"));
      });
    }

    xhr.onload = () => {
      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        text: () => Promise.resolve(xhr.responseText),
      });
    };

    xhr.onerror = () => {
      reject(new Error(SERVER_UNAVAILABLE_MESSAGE));
    };

    xhr.ontimeout = () => {
      reject(new Error("Превышено время ожидания загрузки"));
    };

    xhr.send(formData);
  });
}

function waitBeforeChunkRetry(attempt) {
  return new Promise((resolve) => setTimeout(resolve, 1000 * attempt));
}

async function uploadFileInChunks({ endpoint, file, onProgress, signal }) {
  const totalSize = file.size;
  const totalChunks = Math.ceil(totalSize / CHUNK_SIZE);
  const uploadId =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : `up_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;

  let lastResponse = null;

  for (let chunkIndex = 0; chunkIndex < totalChunks; chunkIndex++) {
    if (signal?.aborted) {
      throw new Error("Загрузка отменена");
    }

    const start = chunkIndex * CHUNK_SIZE;
    const end = Math.min(start + CHUNK_SIZE, totalSize);
    const chunkBlob = file.slice(start, end);

    let attempt = 0;
    let chunkSuccess = false;

    while (attempt < MAX_CHUNK_RETRIES && !chunkSuccess) {
      if (signal?.aborted) throw new Error("Загрузка отменена");
      attempt++;

      try {
        const chunkFormData = new FormData();
        chunkFormData.append("videoFile", chunkBlob, file.name);

        const customHeaders = {
          "x-upload-id": uploadId,
          "x-chunk-index": String(chunkIndex),
          "x-chunk-total": String(totalChunks),
          "x-file-size": String(totalSize),
          "x-file-name": encodeURIComponent(file.name),
        };

        lastResponse = await sendSingleRequestWithProgress({
          endpoint,
          formData: chunkFormData,
          headers: customHeaders,
          signal,
          onProgress: (chunkPercent) => {
            if (typeof onProgress === "function") {
              const uploadedBytes =
                start + chunkBlob.size * (chunkPercent / 100);
              const overallPercent = Math.min(
                100,
                Math.max(0, Math.round((uploadedBytes / totalSize) * 100)),
              );
              onProgress(overallPercent);
            }
          },
        });

        if (lastResponse.ok) {
          chunkSuccess = true;
        } else {
          // If server returned 4xx (e.g. storage quota exceeded), do not retry
          if (lastResponse.status >= 400 && lastResponse.status < 500) {
            return lastResponse;
          }
          if (attempt >= MAX_CHUNK_RETRIES) {
            return lastResponse;
          }
          await waitBeforeChunkRetry(attempt);
        }
      } catch (networkErr) {
        if (attempt >= MAX_CHUNK_RETRIES || signal?.aborted) {
          throw networkErr;
        }
        await waitBeforeChunkRetry(attempt);
      }
    }
  }

  return lastResponse;
}

export function uploadWithProgress({ endpoint, formData, onProgress, signal }) {
  const videoFile = formData?.get ? formData.get("videoFile") : null;
  if (videoFile && typeof videoFile.slice === "function" && videoFile.size > CHUNK_SIZE) {
    return uploadFileInChunks({ endpoint, file: videoFile, onProgress, signal });
  }
  return sendSingleRequestWithProgress({ endpoint, formData, onProgress, signal });
}

async function handleResponse(response) {
  if (response.status === 401) {
    localStorage.removeItem("userData");
    localStorage.removeItem("token");
    window.location.href = "/login";
    throw new Error("Unauthorized");
  }

  if (!response.ok) {
    const error = await response.json();
    throw new Error(error.message || "Request failed");
  }

  return response;
}

export function fetchWithAuth(url, options = {}) {
  return fetch(`${url}`, {
    ...options,
    headers: {
      ...options.headers,
      "Content-Type": "application/json",
      Authorization: setAuthToken(),
    },
  }).then(handleResponse);
}

export const registrationUser = createAsyncThunk(
  "user/register",
  async function (props, { rejectWithValue }) {
    const { login, password } = props;

    try {
      const response = await fetch(`${url}/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          login,
          password,
        }),
      });

      const parseResult = parseAuthJson(await response.text());

      if (parseResult === null) {
        return rejectWithValue({
          type: "error",
          message: "Сервер вернул некорректный ответ. Попробуйте позже.",
        });
      }

      if (!response.ok && parseResult.type !== "error" && parseResult.type !== "info") {
        return rejectWithValue({
          type: "error",
          message:
            parseResult.message ||
            "Не удалось завершить регистрацию. Попробуйте позже.",
        });
      }

      return parseResult;
    } catch {
      return rejectWithValue(authNetworkErrorPayload());
    }
  },
);

export const loginUser = createAsyncThunk(
  "user/login",
  async function (props, { rejectWithValue }) {
    const { login, password } = props;

    try {
      const response = await fetch(`${url}/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          login,
          password,
        }),
      });

      const parseResult = parseAuthJson(await response.text());

      if (parseResult === null) {
        return rejectWithValue({
          type: "error",
          message: "Сервер вернул некорректный ответ. Попробуйте позже.",
        });
      }

      if (!response.ok && parseResult.type !== "error") {
        return rejectWithValue({
          type: "error",
          message:
            parseResult.message || "Не удалось выполнить вход. Попробуйте позже.",
        });
      }

      return parseResult;
    } catch {
      return rejectWithValue(authNetworkErrorPayload());
    }
  },
);

export const validateToken = createAsyncThunk(
  "user/validate",
  async (_, { rejectWithValue }) => {
    try {
      const response = await fetch(`${url}/validate`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: setAuthToken(),
        },
      });

      const result = await response.json();

      if (result.valid && result.user) {
        return {
          user: result.user,
        };
      } else {
        return rejectWithValue(result);
      }
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  },
);

export const getGamesData = createAsyncThunk(
  "user/getGamesData",
  async function (props, dispatch) {
    const { id } = props;

    const response = await fetchWithAuth(`${url}/me/${id}`, {
      method: "GET",
    });
    const result = await response.text();

    if (result.type === "error") {
      return dispatch(userSlice.addMessage);
    } else {
      return JSON.parse(result);
    }
  },
);

export const getGame = createAsyncThunk(`user/getGame`, async function (props) {
  const { gameId } = props;

  const response = await fetchWithAuth(`${url}/game/${gameId}`, {
    method: "GET",
  });

  const responseData = await response.json();

  if (responseData) {
    return responseData;
  }
});

export const addNewGame = createAsyncThunk(
  "user/addNewGame",
  async function (props) {
    const { userId, textProject, contentType, trainerTheme, kbTheme, onSuccess } = props;

    const response = await fetch(`${url}/new`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: setAuthToken(),
      },
      body: JSON.stringify({
        userId,
        textProject,
        contentType,
        trainerTheme,
        kbTheme,
      }),
    });

    const result = await response.json();
    onSuccess?.(result.createdId);
    return result;
  },
);

export const saveTrainerQuestions = createAsyncThunk(
  "user/saveTrainerQuestions",
  async function ({ gameId, trainerTheme, questions }) {
    const response = await fetch(`${url}/trainer/questions`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: setAuthToken(),
      },
      body: JSON.stringify({ gameId, trainerTheme, questions }),
    });

    if (!response.ok) {
      throw new Error("Не удалось сохранить вопросы");
    }

    return response.json();
  },
);

export const saveCourseLessons = createAsyncThunk(
  "user/saveCourseLessons",
  async function ({ gameId, courseTheme, lessons }) {
    const response = await fetch(`${url}/course/lessons`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: setAuthToken(),
      },
      body: JSON.stringify({ gameId, courseTheme, lessons }),
    });

    if (!response.ok) {
      throw new Error("Не удалось сохранить уроки");
    }

    return response.json();
  },
);

export const ensureKnowledgeBase = createAsyncThunk(
  "user/ensureKnowledgeBase",
  async function (_, { rejectWithValue }) {
    const response = await fetch(`${url}/knowledgebase/ensure`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: setAuthToken(),
      },
    });

    const payload = await response.json().catch(() => null);
    if (!response.ok) {
      return rejectWithValue(
        payload ?? { type: "error", message: "Не удалось открыть базу знаний" },
      );
    }

    return payload;
  },
);

export const saveKnowledgeBaseItems = createAsyncThunk(
  "user/saveKnowledgeBaseItems",
  async function ({ gameId, kbTheme, topics, items }) {
    const response = await fetch(`${url}/knowledgebase/items`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: setAuthToken(),
      },
      body: JSON.stringify({ gameId, kbTheme, topics, items }),
    });

    if (!response.ok) {
      throw new Error("Не удалось сохранить базу знаний");
    }

    return response.json();
  },
);

export const addKnowledgeBaseTopic = createAsyncThunk(
  "user/addKnowledgeBaseTopic",
  async function ({ gameId, title, parentId = null }, { rejectWithValue }) {
    const response = await fetch(`${url}/knowledgebase/topics`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: setAuthToken(),
      },
      body: JSON.stringify({ gameId, title, parentId }),
    });

    const payload = await response.json().catch(() => null);
    if (!response.ok) {
      return rejectWithValue(
        payload ?? { type: "error", message: "Не удалось добавить тему" },
      );
    }

    return payload;
  },
);

export const loadKbVideo = createAsyncThunk(
  "user/loadKbVideo",
  async function ({ userId, gameId, itemId, file, onProgress }, { rejectWithValue, signal }) {
    const formData = new FormData();
    formData.append("videoFile", file);

    try {
      const response = await uploadWithProgress({
        endpoint: `${url}/knowledgebase/upload/${userId}/${gameId}/${itemId}`,
        formData,
        onProgress,
        signal,
      });

      const parsed = await parseVideoUploadResponse(response);
      if (!parsed.ok) {
        return rejectWithValue(parsed.payload);
      }

      return parsed.data;
    } catch {
      return rejectWithValue(authNetworkErrorPayload());
    }
  },
);

export const loadSceneImage = createAsyncThunk(
  "user/loadSceneImage",
  async function ({ userId, selectedGameId, scene, file, game }, { rejectWithValue }) {
    const formData = new FormData();
    formData.append("imageFile", file);
    formData.append("gameData", JSON.stringify(game));

    const response = await fetch(
      `${url}/upload-image/${userId}/${selectedGameId}/${scene}`,
      {
        method: "POST",
        headers: {
          Authorization: setAuthToken(),
        },
        body: formData,
      },
    );

    const parsed = await parseVideoUploadResponse(response);
    if (!parsed.ok) {
      return rejectWithValue(parsed.payload);
    }

    return parsed.data;
  },
);

export const loadTrainerImage = createAsyncThunk(
  "user/loadTrainerImage",
  async function ({ userId, gameId, questionId, file }, { rejectWithValue }) {
    const formData = new FormData();
    formData.append("imageFile", file);

    const response = await fetch(
      `${url}/trainer/upload-image/${userId}/${gameId}/${questionId}`,
      {
        method: "POST",
        headers: {
          Authorization: setAuthToken(),
        },
        body: formData,
      },
    );

    const parsed = await parseVideoUploadResponse(response);
    if (!parsed.ok) {
      return rejectWithValue(parsed.payload);
    }

    return parsed.data;
  },
);

export const loadKbImage = createAsyncThunk(
  "user/loadKbImage",
  async function ({ userId, gameId, itemId, file }, { rejectWithValue }) {
    const formData = new FormData();
    formData.append("imageFile", file);

    const response = await fetch(
      `${url}/knowledgebase/upload-image/${userId}/${gameId}/${itemId}`,
      {
        method: "POST",
        headers: {
          Authorization: setAuthToken(),
        },
        body: formData,
      },
    );

    const parsed = await parseVideoUploadResponse(response);
    if (!parsed.ok) {
      return rejectWithValue(parsed.payload);
    }

    return parsed.data;
  },
);

export const loadTrainerVideo = createAsyncThunk(
  "user/loadTrainerVideo",
  async function ({ userId, gameId, questionId, file, onProgress }, { rejectWithValue, signal }) {
    const formData = new FormData();
    formData.append("videoFile", file);

    try {
      const response = await uploadWithProgress({
        endpoint: `${url}/trainer/upload/${userId}/${gameId}/${questionId}`,
        formData,
        onProgress,
        signal,
      });

      const parsed = await parseVideoUploadResponse(response);
      if (!parsed.ok) {
        return rejectWithValue(parsed.payload);
      }

      return parsed.data;
    } catch {
      return rejectWithValue(authNetworkErrorPayload());
    }
  },
);

export const loadCourseVideo = createAsyncThunk(
  "user/loadCourseVideo",
  async function ({ userId, gameId, lessonId, file, onProgress }, { rejectWithValue, signal }) {
    const formData = new FormData();
    formData.append("videoFile", file);

    try {
      const response = await uploadWithProgress({
        endpoint: `${url}/course/upload/${userId}/${gameId}/${lessonId}`,
        formData,
        onProgress,
        signal,
      });

      const parsed = await parseVideoUploadResponse(response);
      if (!parsed.ok) {
        return rejectWithValue(parsed.payload);
      }

      return parsed.data;
    } catch {
      return rejectWithValue(authNetworkErrorPayload());
    }
  },
);

export const deleteGame = createAsyncThunk(
  "user/deleteGame",
  async function (props) {
    const { userId, id, onSucces } = props;

    const response = await fetch(`${url}/delete/${id}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: setAuthToken(),
      },
      body: JSON.stringify({
        userId: userId,
      }),
    });

    const result = await response.text();

    if (result) {
      onSucces?.();

      return JSON.parse(result);
    }
  },
);

export const saveGame = createAsyncThunk(
  "user/saveGame",
  async function (props) {
    const { selectedGameId, nodes, edges } = props;

    const response = await fetch(`${url}/save`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: setAuthToken(),
      },
      body: JSON.stringify({
        data: {
          selectedGameId,
          nodes,
          edges,
        },
      }),
    });

    const result = await response.text();
    return JSON.parse(result);
  },
);

export const saveGameSettings = createAsyncThunk(
  "user/saveGameSettings",
  async function (props, { rejectWithValue }) {
    const {
      game,
      title,
      description,
      category,
      program,
      settings,
      trainerTheme,
      kbTheme,
      courseTheme,
      publishAttempt,
      onSucces,
    } = props;

    const response = await fetch(`${url}/project-settings`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: setAuthToken(),
      },
      body: JSON.stringify({
        data: {
          id: game,
          title,
          description,
          category,
          program,
          settings,
          trainerTheme,
          kbTheme,
          courseTheme,
          publishAttempt,
        },
      }),
    });

    const raw = await response.text();
    let result;

    try {
      result = JSON.parse(raw);
    } catch {
      result = { text: raw };
    }

    if (!response.ok || result?.type === "error") {
      return rejectWithValue({
        type: "error",
        message: result?.message || "Не удалось сохранить настройки",
      });
    }

    onSucces?.();

    return result;
  },
);

export const getLinks = createAsyncThunk(
  "user/getLinks",
  async function (props) {
    const { userId } = props;

    const response = await fetchWithAuth(`${url}/links`, {
      method: "POST",
      body: JSON.stringify({
        data: {
          userId,
        },
      }),
    });

    const result = await response.text();
    return JSON.parse(result);
  },
);

export const loadVideoScene = createAsyncThunk(
  `user/addVideoScene`,
  async function (props, { rejectWithValue, signal }) {
    const { userId, selectedGameId, scene, file, onProgress } = props;

    const formData = new FormData();
    formData.append("videoFile", file);

    try {
      const response = await uploadWithProgress({
        endpoint: `${url}/upload/${userId}/${selectedGameId}/${scene}`,
        formData,
        onProgress,
        signal,
      });

      const parsed = await parseVideoUploadResponse(response);
      if (!parsed.ok) {
        return rejectWithValue(parsed.payload);
      }

      return parsed.data;
    } catch {
      return rejectWithValue(authNetworkErrorPayload());
    }
  },
);

// The server makes the video screenshot in background after the upload response.
export const fetchSceneScreenshotStatus = createAsyncThunk(
  "user/fetchSceneScreenshotStatus",
  async function (props, { rejectWithValue }) {
    const { userId, selectedGameId, scene } = props;

    const response = await fetch(
      `${url}/upload/${userId}/${selectedGameId}/${scene}/screenshot-status`,
      { headers: { Authorization: setAuthToken() } },
    );

    const parsed = await parseVideoUploadResponse(response);
    if (!parsed.ok) {
      return rejectWithValue(parsed.payload);
    }

    return { gameId: String(selectedGameId), sceneId: String(scene), ...parsed.data };
  },
);

export const loadPoster = createAsyncThunk(
  `user/loadPoster`,
  async function (props, { rejectWithValue }) {
    const { gameId, base64String, mimeType, onSucces } = props;
    const dataUrlMatch =
      typeof base64String === "string"
        ? base64String.match(/^data:(image\/[\w.+-]+);base64,([\s\S]+)$/i)
        : null;
    const imageData = dataUrlMatch?.[2] ?? base64String;
    const imageType = mimeType ?? dataUrlMatch?.[1];

    if (typeof imageData !== "string" || !imageData) {
      return rejectWithValue({
        type: "error",
        message: "Не удалось прочитать изображение",
      });
    }

    const formData = new FormData();
    formData.append("imageFile", imageData);
    if (imageType) formData.append("imageType", imageType);

    const response = await fetch(`${url}/upload/${gameId}`, {
      method: "POST",
      headers: {
        Authorization: setAuthToken(),
      },
      body: formData,
    });

    const raw = await response.text();
    let result;
    try {
      result = JSON.parse(raw);
    } catch {
      result = null;
    }

    if (!response.ok || !result || result.type === "error") {
      return rejectWithValue({
        type: "error",
        message: result?.message || "Не удалось загрузить обложку",
      });
    }

    onSucces?.();
    return result;
  },
);

export const deletePoster = createAsyncThunk(
  "user/deletePoster",
  async function (props) {
    const { gameId, onSucces } = props;

    const response = await fetch(`${url}/deletePoster/${gameId}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: setAuthToken(),
      },
    });

    const result = await response.text();

    if (result) {
      onSucces?.();

      return JSON.parse(result);
    }
  },
);

export const addDescription = createAsyncThunk(
  "user/addDescription",
  async function (props) {
    const { selectedGameId, description } = props;

    const response = await fetchWithAuth(`${url}/description`, {
      method: "POST",
      body: JSON.stringify({
        data: {
          selectedGameId,
          description,
        },
      }),
    });

    const result = await response.text();
    return JSON.parse(result);
  },
);

export const getProfile = createAsyncThunk(
  `user/getProfile`,
  async function (props) {
    const { id } = props;

    const response = await fetchWithAuth(`${url}/profile/user/${id}`, {
      method: "GET",
    });

    const result = await response.text();

    return JSON.parse(result);
  },
);

export const changePassword = createAsyncThunk(
  "user/change-password",
  async function (props, { rejectWithValue }) {
    const { userId, currentPassword, newPassword } = props;

    try {
      const response = await fetchWithAuth(`${url}/profile/change-password`, {
        method: "POST",
        body: JSON.stringify({
          userId,
          currentPassword,
          newPassword,
        }),
      });

      const parseResult = parseAuthJson(await response.text());

      if (parseResult === null) {
        return rejectWithValue({
          type: "error",
          message: "Сервер вернул некорректный ответ. Попробуйте позже.",
        });
      }

      if (!response.ok) {
        return rejectWithValue({
          type: parseResult.type ?? "error",
          message:
            parseResult.message ?? "Не удалось изменить пароль. Попробуйте позже.",
        });
      }

      return parseResult;
    } catch {
      return rejectWithValue(authNetworkErrorPayload());
    }
  },
);

export const confirmEmailUser = createAsyncThunk(
  "user/email",
  async function (props) {
    const { id, email } = props;

    const response = await fetchWithAuth(`${url}/profile/email`, {
      method: "POST",
      body: JSON.stringify({
        id,
        email,
      }),
    });

    const result = await response.text();
    const parseResult = JSON.parse(result);

    return parseResult;
  },
);

export const confirmCodeEmail = createAsyncThunk(
  "user/confirmCodeEmail",
  async function (props) {
    const { id, code } = props;

    const response = await fetchWithAuth(`${url}/profile/confirm-email`, {
      method: "POST",
      body: JSON.stringify({
        id,
        code,
      }),
    });

    const result = await response.text();
    const parseResult = JSON.parse(result);

    return parseResult;
  },
);

export const confirmTG = createAsyncThunk(
  "user/generate-tg",
  async function (props) {
    const { id, onSucces } = props;

    const response = await fetch(`${url}/profile/generate-tg`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: setAuthToken(),
      },
      body: JSON.stringify({
        id,
      }),
    });

    const result = await response.text();
    const parseResult = JSON.parse(result);

    onSucces?.();

    return parseResult;
  },
);

export const confirmBirthday = createAsyncThunk(
  "user/birthday",
  async function (props) {
    const { id, date } = props;

    const response = await fetch(`${url}/profile/birthday`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: setAuthToken(),
      },
      body: JSON.stringify({
        id,
        date,
      }),
    });

    const result = await response.text();
    const parseResult = JSON.parse(result);

    return parseResult;
  },
);

// export const createPayment = createAsyncThunk(
//   "user/create-payment",
//   async function (props) {
//     const { id, price } = props;

//     const response = await fetch(`${url}/payment/create-payment`, {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//         Authorization: setAuthToken(),
//       },
//       body: JSON.stringify({
//         id,
//         price,
//       }),
//     });

//     const result = await response.text();
//     const parseResult = JSON.parse(result);

//     return parseResult;
//   }
// );

export const cancelSubscription = createAsyncThunk(
  "user/cancel-subscription",
  async function (props, { rejectWithValue }) {
    const { userId } = props;

    try {
      const response = await fetchWithAuth(`${url}/profile/subscription/cancel`, {
        method: "POST",
        body: JSON.stringify({ userId }),
      });

      const parseResult = JSON.parse(await response.text());

      if (!response.ok) {
        return rejectWithValue({
          type: parseResult.type ?? "error",
          message:
            parseResult.message ??
            "Не удалось отменить автопродление. Попробуйте позже.",
        });
      }

      return parseResult;
    } catch {
      return rejectWithValue(authNetworkErrorPayload());
    }
  },
);

export const resumeSubscription = createAsyncThunk(
  "user/resume-subscription",
  async function (props, { rejectWithValue }) {
    const { userId } = props;

    try {
      const response = await fetchWithAuth(`${url}/profile/subscription/resume`, {
        method: "POST",
        body: JSON.stringify({ userId }),
      });

      const parseResult = JSON.parse(await response.text());

      if (!response.ok) {
        return rejectWithValue({
          type: parseResult.type ?? "error",
          message:
            parseResult.message ??
            "Не удалось включить автопродление. Попробуйте позже.",
        });
      }

      return parseResult;
    } catch {
      return rejectWithValue(authNetworkErrorPayload());
    }
  },
);

export const deleteAccaunt = createAsyncThunk(
  "user/delete-accaunt",
  async function (props) {
    const { userId, onSuccess } = props;

    const response = await fetchWithAuth(`${url}/profile/delete-accaunt`, {
      method: "POST",
      body: JSON.stringify({
        userId,
      }),
    });

    const result = await response.text();
    const parseResult = JSON.parse(result);

    onSuccess?.();

    return parseResult;
  },
);

export const resetPassword = createAsyncThunk(
  "user/reset",
  async function (props, { rejectWithValue }) {
    const { email } = props;

    try {
      const response = await fetch(`${url}/reset`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
        }),
      });

      const parseResult = parseAuthJson(await response.text());

      if (parseResult === null) {
        return rejectWithValue({
          type: "error",
          message: "Сервер вернул некорректный ответ. Попробуйте позже.",
        });
      }

      if (
        !response.ok &&
        parseResult.type !== "error" &&
        parseResult.type !== "info"
      ) {
        return rejectWithValue({
          type: "error",
          message:
            parseResult.message ||
            "Не удалось отправить ссылку. Попробуйте позже.",
        });
      }

      return parseResult;
    } catch {
      return rejectWithValue(authNetworkErrorPayload());
    }
  },
);

export const sendNewUserData = createAsyncThunk(
  "user/reset-password",
  async function (props, { rejectWithValue }) {
    const { token, pass } = props;

    try {
      const response = await fetch(`${url}/reset-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          token,
          pass,
        }),
      });

      const parseResult = parseAuthJson(await response.text());

      if (parseResult === null) {
        return rejectWithValue({
          type: "error",
          message: "Сервер вернул некорректный ответ. Попробуйте позже.",
        });
      }

      if (
        !response.ok &&
        parseResult.type !== "error" &&
        parseResult.type !== "info"
      ) {
        return rejectWithValue({
          type: "error",
          message:
            parseResult.message ||
            "Не удалось сохранить пароль. Попробуйте позже.",
        });
      }

      return parseResult;
    } catch {
      return rejectWithValue(authNetworkErrorPayload());
    }
  },
);

// export const getVideo = createAsyncThunk(
//   `user/getVideo`,
//   async function (props) {
//     const { selectedGameId } = props;

//     const response = await fetch(`${url}/video/${selectedGameId}`, {
//       method: "GET",
//       headers: {
//         "Content-Type": "application/json",
//         Authorization: setAuthToken(),
//       },
//     });

//     const result = await response.text();

//     return JSON.parse(result);
//   }
// );

// export const getNotifications = createAsyncThunk(
//   "user/getNotifications",
//   async function (props) {
//     const { userId } = props;

//     const response = await fetch(`${url}/notifications`, {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//       },
//       body: JSON.stringify({
//         data: {
//           userId,
//         },
//       }),
//     });

//     const result = await response.text();
//     return JSON.parse(result);
//   }
// );

// export const getGamesForModerator = createAsyncThunk(
//   "user/getGamesForModerator",
//   async function (props) {
//     const { id } = props;

//     const response = await fetch(`${url}/moderator/games`, {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//       },
//       body: JSON.stringify({
//         id,
//       }),
//     });

//     const result = await response.text();
//     const parseResult = JSON.parse(result);

//     return parseResult;
//   }
// );

// export const getUsersForModerator = createAsyncThunk(
//   "user/getUsersForModerator",
//   async function (props) {
//     const { id } = props;

//     const response = await fetch(`${url}/moderator/users`, {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//       },
//       body: JSON.stringify({
//         id,
//       }),
//     });

//     const result = await response.text();
//     const parseResult = JSON.parse(result);

//     return parseResult;
//   }
// );

// export const postNews = createAsyncThunk(
//   "user/postNews",
//   async function (props) {
//     const { id, description } = props;

//     const response = await fetch(`${url}/moderator/news`, {
//       method: "POST",
//       headers: {
//         "Content-Type": "application/json",
//       },
//       body: JSON.stringify({
//         id,
//         description,
//       }),
//     });

//     const result = await response.text();
//     const parseResult = JSON.parse(result);

//     return parseResult;
//   }
// );
